// SigningHome.js — Type-9 signing doctor's queue.
//
// Flow:
//   1. Doctor (type 9) logs in via /login → Login.js routes to /signing-home
//   2. This page hits GET /api/users/signingQueue
//      Backend returns patients where
//         uncontrolledValues.__signatureDoctorId === <doctor's signatureDoctorId>
//   3. Each row shows: SL | Patient name | Patient ID | Device | Referring doctor
//      | Report generated | "↗ Open report" button
//   4. Click "Open report" → opens
//      https://reportweb.mybiocalculus.com/?userid=<patient._id> in a new tab
//   5. All actual signing happens on reportweb — master is just the launcher
//
// Sortable by name / id / report date. Searchable across name, device id,
// and patient id. No signature drawing, no per-patient sign action on master.
import React, { Component } from "react";
import axios from "axios";
import { apiUrl } from "../../config/config";
import SideBar from "../common/SideBar";
import { performLogout } from "../../utils/logout";
import "./SigningHome.css";

// Where Open Report sends the doctor. Pick host by which slave the
// patient's ECG data lives on. Backend stamps `ecg_data_location`
// ("web1" | "web2" | "web4" | ...) during sync; the frontend just maps
// it to the right reportweb host. Keep this in sync with the matching
// REPORT_HOST_BY_LOCATION map in the backend route.
const REPORT_HOST_BY_LOCATION = {
  web1: "https://reportweb.mybiocalculus.com",   // legacy / first slave
  web2: "https://reportweb2.mybiocalculus.com",
  web4: "https://reportweb4.mybiocalculus.com",
  web6: "https://reportweb6.mybiocalculus.com",
};
const DEFAULT_REPORT_HOST = "https://reportweb.mybiocalculus.com";

// Build the reportweb URL for a row. Prefers the backend-supplied
// `reportUrl` (so per-slave routing is owned server-side); falls back
// to picking by ecgDataLocation if the backend hasn't been updated yet.
function reportUrlFor(row) {
  if (!row) return DEFAULT_REPORT_HOST;
  if (row.reportUrl) return row.reportUrl;
  var host = REPORT_HOST_BY_LOCATION[row.ecgDataLocation] || DEFAULT_REPORT_HOST;
  return host + "/?userid=" + row.patientId;
}

// Where the published PDF lives. Matches the pattern used in
// VerifierHome.js: a centralised host (`find.mybiocalculus.com`)
// serves all final PDFs regardless of which slave produced them.
const PDF_HOST = "https://find.mybiocalculus.com";

// How many rows show on a single page in the queue table.
const PAGE_SIZE = 20;

// Compute which page numbers to render in the pager.
//   • Up to 7 pages → show them all
//   • More than 7  → show first, current ± neighbours, last; gaps as "…"
// Examples (current page in bold):
//   total=4,  page=2  →  [1, **2**, 3, 4]
//   total=10, page=1  →  [**1**, 2, 3, …, 10]
//   total=10, page=5  →  [1, …, 4, **5**, 6, …, 10]
//   total=10, page=10 →  [1, …, 8, 9, **10**]
function pageWindow(current, total) {
  if (total <= 7) {
    var out = [];
    for (var i = 1; i <= total; i++) out.push(i);
    return out;
  }
  var window = new Set([1, total, current, current - 1, current + 1]);
  // Always show the second + second-last when near the edges
  if (current <= 3) { window.add(2); window.add(3); window.add(4); }
  if (current >= total - 2) {
    window.add(total - 1); window.add(total - 2); window.add(total - 3);
  }
  var pages = Array.from(window).filter(function (n) {
    return n >= 1 && n <= total;
  }).sort(function (a, b) { return a - b; });
  // Insert "…" for gaps
  var withGaps = [];
  for (var k = 0; k < pages.length; k++) {
    if (k > 0 && pages[k] - pages[k - 1] > 1) withGaps.push("…");
    withGaps.push(pages[k]);
  }
  return withGaps;
}

// Build the published-PDF URL for a row. Prefers the backend-supplied
// `pdfUrl`; falls back to constructing it the same way VerifierHome
// does (device_id + name with underscores). If we have no device_id or
// name we hand back the find.mybiocalculus.com lookup URL — the same
// fallback VerifierHome uses in its catch block.
function pdfUrlFor(row) {
  if (!row) return PDF_HOST;
  if (row.pdfUrl) return row.pdfUrl;
  if (row.deviceId && row.patientName) {
    var safeName = String(row.patientName).replace(/\s+/g, "_");
    return PDF_HOST + "/assets/dist/user_records/" + row.deviceId +
           "/Biocalculus_" + safeName + "_" + row.deviceId + ".pdf";
  }
  return PDF_HOST + "/?userid=" + row.patientId;
}

export default class SigningHome extends Component {
  state = {
    bucket:          "pending",   // "pending" | "published"
    rows:            [],          // active bucket's rows
    cachePending:    null,        // null = not loaded yet
    cachePublished:  null,
    countPending:    0,
    countPublished:  0,
    qInput:          "",
    sortKey:         "name",      // "name" | "id" | "createdAt"
    handle:          "",
    loading:         false,
    error:           "",
    page:            1,           // 1-based current page
  };

  componentDidMount() {
    var t = localStorage.getItem("verifiertype");
    if (t !== "3" && t !== "8" && t !== "9") {
      this.setState({ error: "Please sign in with a doctor account (type 3 or 9)." });
      return;
    }
    this.load();
  }

  // Fetch the currently-active bucket from the backend. The endpoint
  // accepts ?bucket=pending|published — pending is the awaiting-signature
  // queue, published is signed-and-delivered reports for this doctor.
  load = async () => {
    var bucket = this.state.bucket;
    this.setState({ loading: true, error: "" });
    try {
      var res  = await axios.get(
        apiUrl + "api/users/signingQueue?bucket=" + bucket
      );
      var rows = res.data.patients || [];
      var update = {
        rows:    rows,
        handle:  res.data.handle || this.state.handle,
        loading: false,
      };
      // Cache so switching tabs is instant once loaded
      if (bucket === "pending") {
        update.cachePending = rows;
        update.countPending = rows.length;
      } else {
        update.cachePublished = rows;
        update.countPublished = rows.length;
      }
      this.setState(update);
    } catch (err) {
      this.setState({
        loading: false,
        error:
          (err.response && err.response.data && err.response.data.error) ||
          err.message || "Failed to load queue",
      });
    }
  };

  // Switch tabs. If the target bucket is already cached, just swap rows;
  // otherwise trigger a fetch.
  setBucket = (bucket) => {
    if (bucket === this.state.bucket) return;
    var cached = bucket === "pending"
      ? this.state.cachePending
      : this.state.cachePublished;
    if (cached) {
      this.setState({ bucket: bucket, rows: cached, qInput: "", page: 1 });
    } else {
      this.setState({ bucket: bucket, rows: [], qInput: "", page: 1 }, this.load);
    }
  };

  // Page nav. Clamps to valid range so we never land on a non-existent page
  // (e.g. user is on page 5 then searches and only 1 page of matches remain).
  setPage = (next, totalPages) => {
    if (next < 1) next = 1;
    if (next > totalPages) next = totalPages;
    if (next === this.state.page) return;
    this.setState({ page: next });
    // Scroll the table back to the top when paging
    try {
      var wrap = document.querySelector(".sh-table-wrap");
      if (wrap) wrap.scrollTop = 0;
    } catch (e) { /* ignore */ }
  };

  // Send the doctor to the right reportweb host (per-slave) with the
  // patient's _id as the userid query param. Opens in a new tab.
  openReport = (row) => {
    var url = reportUrlFor(row);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Open the published PDF in a new tab. Mirrors VerifierHome.js: try
  // the constructed PDF URL; on error fall back to find.mybiocalculus.com.
  openPdf = (row) => {
    var url = pdfUrlFor(row);
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      window.open(PDF_HOST + "/?userid=" + row.patientId, "_blank", "noopener,noreferrer");
    }
  };

  // Log the user out. Delegates to the shared helper so the top-right
  // logout behaves identically to logouts from other pages (clears JWT,
  // resets axios auth header, redirects to /login).
  handleLogout = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    await performLogout();
  };

  filteredAndSorted() {
    var q = this.state.qInput.trim().toLowerCase();
    var rows = !q ? this.state.rows : this.state.rows.filter(function (r) {
      return (
        (r.patientName || "").toLowerCase().indexOf(q) !== -1 ||
        (r.deviceId   || "").toLowerCase().indexOf(q) !== -1 ||
        String(r.patientId).toLowerCase().indexOf(q) !== -1
      );
    });
    // Secondary order: stable by patientId so re-renders don't shuffle
    var k = this.state.sortKey;
    rows = rows.slice().sort(function (a, b) {
      if (k === "name") {
        var an = (a.patientName || "").toLowerCase();
        var bn = (b.patientName || "").toLowerCase();
        if (an !== bn) return an < bn ? -1 : 1;
      } else if (k === "id") {
        var ai = String(a.deviceId || a.patientId || "");
        var bi = String(b.deviceId || b.patientId || "");
        if (ai !== bi) return ai < bi ? -1 : 1;
      } else if (k === "createdAt") {
        var ac = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        var bc = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        if (ac !== bc) return bc - ac;       // newest first
      }
      // Stable tiebreaker
      return String(a.patientId).localeCompare(String(b.patientId));
    });
    return rows;
  }

  render() {
    if (this.state.error) {
      return (
        <div className="sh-root">
          <SideBar
            menus={[{ title: "Reports to sign", url: "/signing-home" }]}
            profileLink="verifier-profile"
          />
          <div className="sh-page">
            <div className="sh-error">{this.state.error}</div>
          </div>
        </div>
      );
    }

    var allRows      = this.filteredAndSorted();
    var isPublished  = this.state.bucket === "published";
    var dateColLabel = isPublished ? "Published" : "Report generated";
    var emptyMsg     = isPublished
      ? "No published reports yet."
      : "No reports pending for you.";

    // ── Pagination ────────────────────────────────────────────────────
    var totalRows   = allRows.length;
    var totalPages  = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
    var page        = Math.min(this.state.page, totalPages);   // clamp
    var firstIndex  = (page - 1) * PAGE_SIZE;
    var lastIndex   = Math.min(firstIndex + PAGE_SIZE, totalRows);
    var rows        = allRows.slice(firstIndex, lastIndex);
    var pageNumbers = pageWindow(page, totalPages);            // e.g. [1,2,3,'…',8,9,10]

    // Pick the date to show in the date column per bucket.
    function dateForRow(r) {
      if (isPublished) {
        // Backend echoes ReportPublished as `reportPublished` (string)
        // and the delivery timestamps as `deliveredAt`. Prefer the
        // human-readable ReportPublished string when present.
        return r.reportPublished || r.deliveredAt || r.reportGeneratedAt || "";
      }
      return r.reportGeneratedAt
        ? new Date(r.reportGeneratedAt).toLocaleString()
        : "—";
    }

    return (
      <div className="sh-root">
        <SideBar
          menus={[{ title: "Reports to sign", url: "/signing-home" }]}
          profileLink="verifier-profile"
          hideProfile={true}
          hideLogout={true}
        />

        <div className="sh-page">
          <div className="sh-header">
            <div>
              <h2>{isPublished ? "My published reports" : "My reports to sign"}</h2>
              <span className="sh-sub">
                {rows.length} {isPublished ? "published" : "pending"}
                {this.state.handle && (
                  <> · signing as <span className="sh-mono">{this.state.handle}</span></>
                )}
              </span>
            </div>
            <div className="sh-header__actions">
              <button
                className="sh-btn sh-btn--ghost"
                onClick={this.load}
                disabled={this.state.loading}
              >
                {this.state.loading ? "Refreshing…" : "↻ Refresh"}
              </button>
              <button
                className="sh-btn sh-btn--logout"
                onClick={this.handleLogout}
                title="Sign out"
              >
                🚪 Logout
              </button>
            </div>
          </div>

          {/* Bucket tabs */}
          <div className="sh-tabs">
            <button
              className={"sh-tab " + (!isPublished ? "sh-tab--active" : "")}
              onClick={() => this.setBucket("pending")}
            >
              Pending
              {this.state.countPending > 0 && (
                <span className="sh-tab__count">{this.state.countPending}</span>
              )}
            </button>
            <button
              className={"sh-tab " + (isPublished ? "sh-tab--active" : "")}
              onClick={() => this.setBucket("published")}
            >
              Published
              {this.state.countPublished > 0 && (
                <span className="sh-tab__count">{this.state.countPublished}</span>
              )}
            </button>
          </div>

          <div className="sh-filters">
            <input
              className="sh-search"
              type="text"
              placeholder="Search patient name, device id, patient id…"
              value={this.state.qInput}
              onChange={(e) => this.setState({ qInput: e.target.value, page: 1 })}
            />
            <select
              className="sh-search"
              style={{ flex: "none", width: 220 }}
              value={this.state.sortKey}
              onChange={(e) => this.setState({ sortKey: e.target.value, page: 1 })}
            >
              <option value="name">Sort: Patient name (A–Z)</option>
              <option value="id">Sort: Patient / device id</option>
              <option value="createdAt">Sort: Registered date (newest)</option>
            </select>
          </div>

          <div className="sh-table-wrap">
            <table className="sh-table">
              {/* <colgroup> pins column widths regardless of cell content.
                  Browsers honor <col width="..."> stricter than CSS percentages
                  on <th> — avoids the "long text steals width" problem. */}
              <colgroup>
                <col className="sh-col-sl" />
                <col className="sh-col-name" />
                <col className="sh-col-pid" />
                <col className="sh-col-regdate" />
                <col className="sh-col-refdoc" />
                <col className="sh-col-action" />
                <col className="sh-col-date" />
              </colgroup>
              <thead>
                <tr>
                  <th className="sh-col-sl">SL</th>
                  <th className="sh-col-name">Patient name</th>
                  <th className="sh-col-pid">Patient ID</th>
                  <th className="sh-col-regdate">Registration date</th>
                  <th className="sh-col-refdoc">Referring doctor</th>
                  <th className="sh-col-action">Action</th>
                  <th className="sh-col-date">{dateColLabel}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !this.state.loading && (
                  <tr>
                    <td colSpan="7" className="sh-empty">
                      {emptyMsg}
                    </td>
                  </tr>
                )}
                {rows.map((r, idx) => (
                  <tr key={r.patientId}>
                    <td className="sh-col-sl">{firstIndex + idx + 1}</td>
                    <td className="sh-name sh-col-name">{r.patientName || "—"}</td>
                    <td className="sh-mono sh-col-pid" title={r.patientId}>
                      {r.deviceId || "—"}
                    </td>
                    <td className="sh-col-regdate">
                      {r.createdAt
                        ? new Date(r.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="sh-col-refdoc">{r.referringDoctor || "—"}</td>
                    <td className="sh-col-action">
                      {isPublished ? (
                        <button
                          className="sh-btn sh-btn--sm"
                          onClick={() => this.openPdf(r)}
                          title={pdfUrlFor(r)}
                        >
                          📄 View PDF
                        </button>
                      ) : (
                        <button
                          className="sh-btn sh-btn--sm"
                          onClick={() => this.openReport(r)}
                          title={reportUrlFor(r)}
                        >
                          ↗ Open report
                        </button>
                      )}
                    </td>
                    <td className="sh-col-date">{dateForRow(r) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pagination footer ────────────────────────────────────── */}
          {totalRows > 0 && (
            <div className="sh-pager">
              <div className="sh-pager__info">
                Showing <b>{firstIndex + 1}</b>–<b>{lastIndex}</b> of{" "}
                <b>{totalRows}</b>
              </div>
              <div className="sh-pager__controls">
                <button
                  className="sh-pager__btn"
                  onClick={() => this.setPage(page - 1, totalPages)}
                  disabled={page === 1}
                  title="Previous page"
                >
                  ‹ Prev
                </button>
                {pageNumbers.map(function (p, i) {
                  if (p === "…") {
                    return (
                      <span key={"gap-" + i} className="sh-pager__gap">…</span>
                    );
                  }
                  return (
                    <button
                      key={p}
                      className={
                        "sh-pager__btn" +
                        (p === page ? " sh-pager__btn--active" : "")
                      }
                      onClick={() => this.setPage(p, totalPages)}
                    >
                      {p}
                    </button>
                  );
                }.bind(this))}
                <button
                  className="sh-pager__btn"
                  onClick={() => this.setPage(page + 1, totalPages)}
                  disabled={page === totalPages}
                  title="Next page"
                >
                  Next ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}
